import type { AcceptedException, AcceptedValue } from '../types/prioritization/itemLifecycle';
import { valueCountsFor } from './valueScope';

export interface ValueSufficiencyResult {
  valuesSufficient: boolean;
  valueCompletionByAttribute: Record<string, number>;
  minimumValueCompletionPercent: number;
}

interface ComputeValueSufficiencyParams {
  itemIds: string[];
  attributeKeys: string[];
  acceptedValues: AcceptedValue[];
  exceptions: AcceptedException[];
  sessionId?: string;
}

export const DEFAULT_MINIMUM_VALUE_COMPLETION_PERCENT = 100;

function hasAcceptedValue(
  acceptedValues: AcceptedValue[],
  itemId: string,
  attributeKey: string,
  sessionId?: string
): boolean {
  return acceptedValues.some((acceptedValue) => {
    if (acceptedValue.itemId !== itemId || acceptedValue.attributeKey !== attributeKey) {
      return false;
    }
    return valueCountsFor(acceptedValue, sessionId);
  });
}

function hasAcceptedException(
  exceptions: AcceptedException[],
  itemId: string,
  attributeKey: string,
  sessionId?: string
): boolean {
  return exceptions.some((exception) => {
    if (exception.itemId !== itemId || exception.attributeKey !== attributeKey) {
      return false;
    }

    if (sessionId) {
      return exception.sessionId === sessionId || exception.scope === 'global';
    }

    return exception.scope === 'global';
  });
}

export function computeValueSufficiency({
  itemIds,
  attributeKeys,
  acceptedValues,
  exceptions,
  sessionId,
}: ComputeValueSufficiencyParams): ValueSufficiencyResult {
  if (itemIds.length === 0 || attributeKeys.length === 0) {
    return {
      valuesSufficient: true,
      valueCompletionByAttribute: {},
      minimumValueCompletionPercent: DEFAULT_MINIMUM_VALUE_COMPLETION_PERCENT,
    };
  }

  const valueCompletionByAttribute: Record<string, number> = {};

  for (const attributeKey of attributeKeys) {
    let completedItems = 0;

    for (const itemId of itemIds) {
      if (
        hasAcceptedValue(acceptedValues, itemId, attributeKey, sessionId) ||
        hasAcceptedException(exceptions, itemId, attributeKey, sessionId)
      ) {
        completedItems += 1;
      }
    }

    valueCompletionByAttribute[attributeKey] = Math.round((completedItems / itemIds.length) * 100);
  }

  return {
    valuesSufficient: Object.values(valueCompletionByAttribute).every(
      (percent) => percent >= DEFAULT_MINIMUM_VALUE_COMPLETION_PERCENT
    ),
    valueCompletionByAttribute,
    minimumValueCompletionPercent: DEFAULT_MINIMUM_VALUE_COMPLETION_PERCENT,
  };
}
